import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Icon } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { Spinner } from '~/components/Spinner';
import { useArtistDetailsQuery } from '~/modules/artist-roster/hooks/useArtistDetailsQuery';
import { TrackHeader } from '~/modules/artist-roster/pages/track-details/components/TrackHeader/TrackHeader';
import { useTrackDetailsQuery } from '~/modules/artist-roster/pages/track-details/hooks/useTrackDetailsQuery';
import { BREADCRUMBS_PROPS } from '~/modules/constants';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

import { PerformanceView } from './components/PerformanceView/PerformanceView';

export function TrackDetailsPage() {
  const { t } = useTranslation('roster');
  const { partyId } = useParams<{ partyId: string }>();
  const { familyId } = useParams<{ familyId: string }>();
  const { data: artistData } = useArtistDetailsQuery({ partyId });

  const {
    data: trackData,
    error: tracksError,
    isFetching: isFetchingTracks,
  } = useTrackDetailsQuery({ familyId });

  const artistName = artistData?.name;

  if (tracksError) {
    return (
      <DataUnavailable
        title={t('errors.loadingTrackDetails')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  if (!partyId || isNaN(Number(partyId))) {
    return (
      <div className="flex w-full items-center justify-start p-4xl">
        <p className="m-0 font-header text-display-xl leading-none text-default">
          {t('errors.invalidArtistId')}
        </p>
      </div>
    );
  }

  let content;

  if (isFetchingTracks) {
    content = (
      <div className="flex w-full items-center justify-center p-2xl">
        <Spinner />
      </div>
    );
  } else if (trackData) {
    content = (
      <div className="flex w-full flex-col items-start gap-lg">
        <TrackHeader artistData={artistData} trackData={trackData} />
        <PerformanceView trackData={trackData} />
      </div>
    );
  } else {
    content = null;
  }

  return (
    <div className="flex flex-col items-center justify-between p-xl">
      <div className="flex w-full flex-col items-center justify-between gap-lg">
        <Breadcrumbs.Root {...BREADCRUMBS_PROPS.ROOT}>
          <Breadcrumbs.Item to="/roster" {...BREADCRUMBS_PROPS.ITEM_LINK}>
            {t('header.heading')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item
            to={`/roster/${partyId}`}
            {...BREADCRUMBS_PROPS.ITEM_LINK}
          >
            {artistName}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>{trackData?.title || ''}</Breadcrumbs.Item>
        </Breadcrumbs.Root>
        {content}
      </div>
    </div>
  );
}

TrackDetailsPage.displayName = 'TrackDetailsPage';
